import { push } from 'connected-react-router';
import { toastr } from 'react-redux-toastr';
import { defineMessages } from 'react-intl';
import {
  call,
  put,
  race,
  select,
  take,
} from 'redux-saga/effects';
import {
  toggleModal,
  TOGGLE_MODAL,
} from '../actions/modals';
import { getProfile } from '../actions/profile';
import {
  autoSigninSuccess,
  AUTO_SIGNIN,
} from '../actions/signin';
import { setContainerLoadState } from '../actions/container-load-state';
import { displayToastr } from '../actions/toastr';
import ApiResource from '../api-resources';
import {
  userSelector,
  modalSelector,
} from '../selectors';
import { needFillLanguageSelector } from '../selectors/profile';
import { isNecessaryToCropAvatarSelector } from '../selectors/user';
import {
  createSuccessActionType,
  createErrorActionType,
  createRequestActionType,
} from '../utils/actions';
import {
  serverToClient,
} from '../utils/error';
import { isModalClosed } from '../utils/modal';
import { t } from '../utils/translate';
import Routes from '../routes';
import { authenticate } from '../scenes/onboarding/signin/sagas/signin';
import { displayPendingReviews } from './appointments';

export const signinSagaTranslations = defineMessages({
  errorCannotReconnect: {
    id: 'toastr.signin.errorCannotReconnect',
    defaultMessage: 'Nous n\'avons pas pu vous reconnecter',
    description: 'Erreur de reconnexion',
  },
  userEmailConfirmationError: {
    id: 'toastr.signin.userEmailConfirmationError',
    defaultMessage: 'Désolé, une erreur est survenue. Veuillez essayer à nouveau.',
    description: 'Message lors de la demande de renvoi du mail de confirmation',
  },
  errorCropAvatarToastr: {
    id: 'toastr.signin.errorCropAvatar',
    defaultMessage: 'Vous avez rencontré une erreur lors du recadrage de votre photo. Veuillez réessayer ou nous contacter.',
    description: 'Message d erreur lorsque l utilisateur echoue à cropper son avatar',
  },
  oauthEmailAlreadyUsed: {
    id: 'toastr.signin.oauthEmailAlreadyUsed',
    defaultMessage: 'Un compte existe déjà avec votre adresse email LinkedIn. Merci de vous y connecter avec votre mot de passe',
    description: 'Error when user not found',
  },
});

/**
 * It's very important for the futher to rework theses sagas
 * Too many dead code and maze functions
 * @url https://github.com/MyJobGlasses/Hermes/issues/4081
 */

/* Auto-Signin
 * Responsible for exchanging user credentials (ps, linkedin, etc.)
 * against Json Web Token
 *
 * If you are looking for session-restoration mechanism
 * You should look at src/sagas/storage.js
 */
export function* autoSignin() {
  while (true) { // eslint-disable-line no-constant-condition
    const authenticationInformation = yield take(AUTO_SIGNIN);
    const { authFlow, flavor } = authenticationInformation;
    // Note: call(authenticate) may also return { identity, error }
    // but they are not used in autosignin
    const { credentials } = yield call(authenticate, authenticationInformation, { authFlow, flavor });

    if (credentials) {
      const user = yield select(userSelector);
      yield put(getProfile());

      const { success, error } = yield race({
        success: take('@@api/READ_RESOURCE_USER_SUCCESS'),
        error: take('@@api/READ_RESOURCE_USER_ERROR'),
      });

      if (success) {
        yield put(autoSigninSuccess({ flow: user.type }));
        yield call(displayPendingReviews);
      } else {
        console.error(error); // eslint-disable-line no-console
        toastr.error(serverToClient(error));
      }
    } else {
      toastr.error(t(signinSagaTranslations.errorCannotReconnect));
    }
  }
}

/**
 * request to user to confirm his email before resend confirmation email
 */
export function* requestUserEmailConfirmation() {
  yield put(toggleModal('requestUserEmailConfirmation', true));
  const requestData = yield take(createRequestActionType('CREATE', ApiResource.USER_CONFIRMATION));
  yield put(setContainerLoadState('requestUserEmailConfirmation', { sending: true }));
  const { success, error } = yield race({
    success: take(createSuccessActionType('CREATE', ApiResource.USER_CONFIRMATION)),
    error: take(createErrorActionType('CREATE', ApiResource.USER_CONFIRMATION)),
    abort: take(isModalClosed('requestUserEmailConfirmation')),
  });
  if (success) {
    yield put(setContainerLoadState('requestUserEmailConfirmation', { success: requestData?.data?.attributes?.email || '' }));
    return;
  } else if (error) {
    yield put(setContainerLoadState('requestUserEmailConfirmation', { sending: false }));
    yield put(displayToastr('error', t(signinSagaTranslations.userEmailConfirmationError)));
  }
}

/**
 * Display requestLanguage modal if necessary
 */
export function* requestIfNecessaryLanguages() {
  const modal = yield select(modalSelector, 'signup');
  const isSignupModalOpened = modal?.isOpened;
  if (isSignupModalOpened) {
    return;
  }
  const needFillLanguage = yield select(needFillLanguageSelector);
  if (needFillLanguage) {
    yield put(toggleModal('requestLanguage', true));
    yield take(TOGGLE_MODAL);
  }
}

/**
 * Check if attributes mustResizeAvatar is set on user
 * Then, start to request avatar to user
 */
export function* requestIfNecessaryAvatarCropInformations() {
  const isNecessaryToCrop = yield select(isNecessaryToCropAvatarSelector);
  if (isNecessaryToCrop) {
    yield put(push(Routes.profileAvatarEdit));
  }
}
